/* @flow */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

// Styles
import styles from 'src/shared/app/base/component/infiniteScoll/InfiniteScroll.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import messages from 'src/shared/app/base/l10n/uiL10n';
import useEventListener from 'src/shared/app/base/component/hook/useEventListener';
import Button, { VARIANT } from 'src/shared/ui/component/button/Button';

const defaultThreshold = 250;

type Props = {
  children: React.Node,
  itemsLeft?: number | null,
  threshold?: number,
  onNext?: () => any,
  manualConfig?: { isActive: boolean, variant?: $Values<typeof VARIANT> },
  isLazyFetching?: boolean,
};

const useStyles = createUseThemeStyles(styles);

const InfiniteScroll = ({
  children,
  itemsLeft,
  threshold = defaultThreshold,
  onNext,
  manualConfig = {
    isActive: false,
    variant: VARIANT.PRIMARY,
  },
  isLazyFetching,
}: Props): React.Node => {
  const classes = useStyles();
  const ref = React.useRef();
  const intl = useIntl();

  const onWindowScroll = useDebouncedCallback(() => {
    const { current: feedElt } = ref;
    if (manualConfig.isActive) return;

    if (onNext && feedElt instanceof HTMLElement) {
      const { top, height } = feedElt.getBoundingClientRect();
      const y = top + height - window.innerHeight;
      const delta = threshold || 0;

      if (y < delta) {
        onNext();
      }
    }
  }, 100);

  useEventListener('scroll', onWindowScroll);

  const manualLoadElt = manualConfig.isActive && onNext && (
    <div className={classes.manualAction}>
      <Button
        onClick={onNext}
        isFetching={isLazyFetching}
        isDisabled={isLazyFetching}
        variant={manualConfig.variant || VARIANT.PRIMARY}
      >
        {intl.formatMessage(
          itemsLeft ? messages.loadMoreItemsLeft : messages.loadMore,
          { itemsLeft },
        )}
      </Button>
    </div>
  );

  return (
    <div ref={ref}>
      {children}
      {manualLoadElt}
    </div>
  );
};

export default React.memo<Props>(InfiniteScroll);
